import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAgents } from "../../context/AgentsContext";
import useDebounce from "../../../../../hooks/useDebounce";
import { LAST_UPDATE_PRESETS, getDateRangeForPreset } from "./formatters";
import dayjs from "dayjs";

export const useAgentsPage = () => {
  // ============================================================================
  // External Hooks
  // ============================================================================
  const {
    baseAgents,
    baseMeta,
    agents,
    agentsMeta,
    loading,
    error,
    fetchAgents,
    resetAgentsToBase,
    resetAgents,
  } = useAgents();

  // ============================================================================
  // State - Filter States
  // ============================================================================
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [lastUpdatePreset, setLastUpdatePreset] = useState("");
  const [lastUpdateRange, setLastUpdateRange] = useState({ from: null, to: null });

  // ============================================================================
  // Debounced Search Value
  // ============================================================================
  const debouncedSearchValue = useDebounce(searchValue);

  // ============================================================================
  // State - Pagination & Sorting
  // ============================================================================
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDirection, setOrderDirection] = useState("DESC");

  // ============================================================================
  // State - UI State (Keys and Local Searches)
  // ============================================================================
  const [searchKey, setSearchKey] = useState(0);
  const [filtersKey, setFiltersKey] = useState(0);
  const [localSearches, setLocalSearches] = useState({
    status: "",
    type: "",
    category: "",
    lastUpdate: "",
  });

  // ============================================================================
  // Debounced Local Searches (for Select.Search filters)
  // ============================================================================
  const debouncedLocalSearches = {
    status: useDebounce(localSearches.status),
    type: useDebounce(localSearches.type),
    category: useDebounce(localSearches.category),
    lastUpdate: useDebounce(localSearches.lastUpdate),
  };

  // ============================================================================
  // State - Internal State
  // ============================================================================
  const [hasLoadedBase, setHasLoadedBase] = useState(false);
  const prevSanitizedSearchRef = useRef(undefined);

  // ============================================================================
  // Helper Functions
  // ============================================================================
  const filterOptions = useCallback((options, searchTerm) => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchLower) ||
        option.value?.toLowerCase().includes(searchLower)
    );
  }, []);

  // ============================================================================
  // useMemo - Base Options
  // ============================================================================
  const statusOptions = useMemo(
    () => [
      { label: "Active", value: "active" },
      { label: "Disabled", value: "disabled" },
      { label: "Draft", value: "draft" },
      { label: "Archived", value: "archived" },
    ],
    []
  );

  const typeOptions = useMemo(
    () => [
      { label: "AI", value: "ai" },
    ],
    []
  );

  // const categoryOptions = useMemo(
  //   () => [
  //     { label: "GPT-4", value: "gpt-4" },
  //     { label: "GPT-4 Turbo", value: "gpt-4-turbo" },
  //     { label: "GPT-3.5", value: "gpt-3.5" },
  //     { label: "Claude 2", value: "claude-2" },
  //   ],
  //   []
  // );
  const categoryOptions = useMemo(
    () => [
      { label: "Service", value: "Service" },
    ],
    []
  );


  const lastUpdateOptions = useMemo(() => LAST_UPDATE_PRESETS, []);

  // ============================================================================
  // useMemo - Filtered Options (for Select.Search)
  // ============================================================================
  const filteredStatus = useMemo(
    () => filterOptions(statusOptions, debouncedLocalSearches.status),
    [statusOptions, debouncedLocalSearches.status, filterOptions]
  );

  const filteredType = useMemo(
    () => filterOptions(typeOptions, debouncedLocalSearches.type),
    [typeOptions, debouncedLocalSearches.type, filterOptions]
  );

  const filteredCategory = useMemo(
    () => filterOptions(categoryOptions, debouncedLocalSearches.category),
    [categoryOptions, debouncedLocalSearches.category, filterOptions]
  );

  const filteredLastUpdate = useMemo(
    () => filterOptions(lastUpdateOptions, debouncedLocalSearches.lastUpdate),
    [lastUpdateOptions, debouncedLocalSearches.lastUpdate, filterOptions]
  );

  // ============================================================================
  // useMemo - Derived Data (for API calls)
  // ============================================================================
  const filtersPayload = useMemo(() => {
    const payload = {};

    if (statusFilter.length > 0) {
      payload.status = statusFilter;
    }

    if (typeFilter.length > 0) {
      payload.type = typeFilter;
    }

    if (categoryFilter.length > 0) {
      payload.category = categoryFilter;
    }

    if (lastUpdateRange.from || lastUpdateRange.to) {
      payload.createdAt = {};
      if (lastUpdateRange.from) {
        const date = dayjs(lastUpdateRange.from).toISOString();
        payload.createdAt.from = date;
      }
      if (lastUpdateRange.to) {
        const date = dayjs(lastUpdateRange.to).endOf("day").toISOString();
        payload.createdAt.to = date;
      }
    }

    return Object.keys(payload).length ? payload : undefined;
  }, [statusFilter, typeFilter, categoryFilter, lastUpdateRange]);

  const sanitizedSearch = useMemo(() => {
    const trimmed = debouncedSearchValue.trim();
    return trimmed.length
      ? {
          query: trimmed,
          fields: ["name"],
        }
      : undefined;
  }, [debouncedSearchValue]);

  // ============================================================================
  // useMemo - Check if there are active filters
  // ============================================================================
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      sanitizedSearch ||
      statusFilter.length > 0 ||
      typeFilter.length > 0 ||
      categoryFilter.length > 0 ||
      lastUpdatePreset
    );
  }, [
    sanitizedSearch,
    statusFilter,
    typeFilter,
    categoryFilter,
    lastUpdatePreset,
  ]);

  // ============================================================================
  // Utility Callbacks
  // ============================================================================
  const resetLocalState = useCallback(() => {
    setSearchValue("");
    setStatusFilter([]);
    setTypeFilter([]);
    setCategoryFilter([]);
    setLastUpdatePreset("");
    setLastUpdateRange({ from: null, to: null });
    setPage(1);
    setPageSize(20);
    setOrderBy("created_at");
    setOrderDirection("DESC");
  }, []);

  const resetLocalSearches = useCallback(() => {
    setLocalSearches({
      status: "",
      type: "",
      category: "",
      lastUpdate: "",
    });
  }, []);

  const updateLocalSearch = useCallback((filterType, value) => {
    setLocalSearches((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  // ============================================================================
  // useEffect Hooks
  // ============================================================================
  useEffect(() => {
    const hasFilters = Boolean(filtersPayload || sanitizedSearch);
    const hasSorting =
      orderBy !== "created_at" || orderDirection !== "DESC";
    const hasPaginationChanges = page !== 1 || pageSize !== 20;

    // Detectar cuando el search cambia de tener valor a ser undefined
    const searchWasCleared =
      prevSanitizedSearchRef.current !== undefined &&
      sanitizedSearch === undefined;

    const params = {
      page,
      pageSize,
      orderBy,
      orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch,
    };

    if (!hasFilters && !hasSorting && !hasPaginationChanges) {
      if (!hasLoadedBase) {
        if (baseAgents.length) {
          resetAgentsToBase();
          setHasLoadedBase(true);
          prevSanitizedSearchRef.current = sanitizedSearch;
          return;
        }

        fetchAgents(params, { persistAsBase: true });
        setHasLoadedBase(true);
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      // Si el search fue borrado y no hay otros filtros, resetear a base
      if (searchWasCleared && !filtersPayload) {
        resetAgentsToBase();
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      prevSanitizedSearchRef.current = sanitizedSearch;
      return;
    }

    fetchAgents(params, { persistAsBase: false });
    prevSanitizedSearchRef.current = sanitizedSearch;
  }, [
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchAgents,
    hasLoadedBase,
    baseAgents.length,
    resetAgentsToBase,
  ]);

  // ============================================================================
  // Handler Callbacks
  // ============================================================================
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value) => {
    // Convertir a array si es necesario
    const items = Array.isArray(value) ? value : (value ? [value] : []);
    setStatusFilter(items);
    setPage(1);
  }, []);

  const handleTypeChange = useCallback((value) => {
    // Convertir a array si es necesario
    const items = Array.isArray(value) ? value : (value ? [value] : []);
    setTypeFilter(items);
    setPage(1);
  }, []);

  const handleCategoryChange = useCallback((value) => {
    // Convertir a array si es necesario
    const items = Array.isArray(value) ? value : (value ? [value] : []);
    setCategoryFilter(items);
    setPage(1);
  }, []);

  const handleLastUpdatePresetChange = useCallback((value) => {
    setLastUpdatePreset(value || "");
    setLastUpdateRange(getDateRangeForPreset(value || ""));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  const handleClearFiltersWithKeys = useCallback(() => {
    resetLocalState();
    resetLocalSearches();
    setSearchKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);
    resetAgentsToBase();
  }, [resetLocalState, resetLocalSearches, resetAgentsToBase]);

  const handleRefresh = useCallback(() => {
    const params = {
      page,
      pageSize,
      orderBy,
      orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch,
    };
    fetchAgents(params, { persistAsBase: false });
  }, [
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchAgents,
  ]);

  // ============================================================================
  // Return Object
  // ============================================================================
  return {
    loading,
    error,
    agents,
    agentsMeta,
    filters: {
      statusOptions,
      typeOptions,
      categoryOptions,
      lastUpdateOptions,
      filteredStatus,
      filteredType,
      filteredCategory,
      filteredLastUpdate,
      selectedStatus: statusFilter,
      selectedType: typeFilter,
      selectedCategory: categoryFilter,
      selectedLastUpdatePreset: lastUpdatePreset,
      searchValue,
      hasActiveFilters,
    },
    filterHandlers: {
      handleSearchChange,
      handleStatusChange,
      handleTypeChange,
      handleCategoryChange,
      handleLastUpdatePresetChange,
      handleClearFiltersWithKeys,
    },
    filterUI: {
      searchKey,
      filtersKey,
      updateLocalSearch,
    },
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
  };
};

